import React, { useState, useEffect, useRef } from "react";
import { Modal, Input, List, Spin, Empty, message, Space } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { getObjective } from "../../services/salesforceProjectApi";
import { searchProjectObjectives } from "../../services/projectObjectiveService";
import { useNavigate } from "react-router-dom";
import CreateProjectObjectiveModal from "./CreateProjectObjectiveModal";

export default function CloneProjectObjectiveModal({
  visible,
  onClose,
  onSuccess,
  objectiveId = "",
  objectiveName = "",
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const debounceRef = useRef(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [initialForm, setInitialForm] = useState(null);

  // Search objectives with debounce
  useEffect(() => {
    if (objectiveId) {
      setSelectedObjective({ Id: objectiveId, Name: objectiveName });
    }
    const term = (search || "").trim();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (term.length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const objectiveList = await searchProjectObjectives(term);
        const termLower = term.toLowerCase();
        const filtered = (
          Array.isArray(objectiveList) ? objectiveList : []
        ).filter((obj) => {
          const name = obj?.Name ? String(obj.Name).toLowerCase() : "";
          const id = obj?.Id ? String(obj.Id).toLowerCase() : "";
          return name.includes(termLower) || id.includes(termLower);
        });
        if (filtered.length > 0) {
          setResults(filtered);
        } else {
          // Fallback: if term looks like a Salesforce Id, try direct fetch by id
          const looksLikeId = /^[a-zA-Z0-9]{15}([a-zA-Z0-9]{3})?$/.test(
            term.trim(),
          );
          if (looksLikeId) {
            const direct = await getObjective(term.trim());
            // Accept common response shapes
            const pick = Array.isArray(direct)
              ? direct[0]
              : direct?.data && Array.isArray(direct.data)
                ? direct.data[0]
                : direct?.data?.Id
                  ? direct.data
                  : direct?.Id
                    ? direct
                    : null;
            setResults(pick ? [pick] : []);
          } else {
            setResults([]);
          }
        }
      } catch (error) {
        message.error("Failed to search objectives");
        console.error(error);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, objectiveId]);

  // Handle objective selection
  const handleSelectObjective = (objective) => {
    setSelectedObjective(objective);
  };

  // Handle "clone" by opening the Create Objective modal prefilled
  const handleClone = async () => {
    if (!selectedObjective) {
      message.warning("Please select a project objective to clone");
      return;
    }

    setCloning(true);
    try {
      // Fetch full objective details for better prefill
      const raw = await getObjective(selectedObjective.Id);
      const rec =
        (Array.isArray(raw?.data) && raw.data[0]) ||
        (Array.isArray(raw) && raw[0]) ||
        raw?.data ||
        raw ||
        null;

      // Map Salesforce record to Create form shape
      const prefill = {
        objectiveName: rec?.Name || "",
        contributorFacingName: rec?.Contributor_Facing_Project_Name__c || "",
        appendToObjectiveName: rec?.Append_to_Objective_Name__c || "",
        dateStart: rec?.Date_Start__c || "",
        dateEnd: rec?.Date_End__c || "",
        selectionCriteria: rec?.Selection_Criteria__c || "",
        targetContributors: rec?.Target_Contributors__c ?? "",
        country: rec?.Country__c || "",
        language: rec?.Language__c || "",
        workType: rec?.Work_Type__c || "",
        languageSkillLevel: rec?.Language_Skill_Level__c || "",
        degreeRequirement: rec?.Degree_Requirement__c || "",
        additionalManualMatchRequired: Boolean(
          rec?.Additional_Manual_Match_Required__c,
        ),
        productivityTargetType: rec?.Productivity_Target_Type__c || "",
        minimumContributors: rec?.Minimum_Contributors__c ?? "",
        daysBetweenReminderEmails: rec?.Days_Between_Reminder_Emails__c ?? "",
        status: rec?.Status__c || "Draft",
        // Do not bind project/objective ids here to avoid linking to old project
      };

      setInitialForm(prefill);
      setShowCreateModal(true);

      // Reset and close search modal
      setSearch("");
      setResults([]);
      setSelectedObjective(null);
      onClose && onClose();

      message.info(
        "Creation form opened with objective details. Review and Save.",
      );
    } catch (error) {
      message.error(
        "Failed to prepare objective: " +
          (error.response?.data?.error?.message || error.message),
      );
      console.error("Prefill error:", error);
    } finally {
      setCloning(false);
    }
  };

  const handleCancel = () => {
    setSearch("");
    setResults([]);
    setSelectedObjective(null);
    onClose();
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <CopyOutlined />
            <span>Clone Project Objective</span>
          </Space>
        }
        open={visible}
        onCancel={handleCancel}
        onOk={handleClone}
        okText="Clone Objective"
        okButtonProps={{
          disabled: !selectedObjective || cloning,
          loading: cloning,
        }}
        width={700}
        bodyStyle={{ padding: 16 }}
      >
        <div className="space-y-4">
          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search for a project objective to clone (by name or ID):
            </label>
            <Input.Search
              placeholder="Search objectives by name or ID..."
              value={objectiveName || search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              size="large"
              loading={loading}
            />
          </div>

          {/* Objective Results */}
          <div
            style={{
              maxHeight: "300px",
              overflowY: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: "4px",
              padding: "8px",
            }}
          >
            {loading ? (
              <div className="text-center py-8">
                <Spin tip="Searching objectives..." />
              </div>
            ) : results.length > 0 ? (
              <List
                dataSource={results}
                renderItem={(item) => (
                  <List.Item
                    className={`cursor-pointer transition-colors px-4 ${
                      selectedObjective?.Id === item.Id
                        ? "bg-blue-50"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => handleSelectObjective(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <List.Item.Meta
                      title={
                        <div className="flex items-center">
                          {selectedObjective?.Id === item.Id && (
                            <CopyOutlined className="mr-2 text-blue-500" />
                          )}
                          <span className="font-medium">{item.Name}</span>
                        </div>
                      }
                      description={
                        <div className="text-sm text-gray-500">
                          <div>Status: {item.Status__c || "N/A"}</div>
                          {item.Project__r?.Name && (
                            <div>Project: {item.Project__r.Name}</div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : search.length >= 3 ? (
              <Empty description="No objectives found" className="py-8" />
            ) : (
              <Empty
                description="Type at least 3 characters to search"
                className="py-8"
              />
            )}
          </div>

          {/* No clone options shown; this flow opens the creation form prefilled for the selected Objective only */}

          {/* Selected Objective Info */}
          {selectedObjective && (
            <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
              <div className="text-sm">
                <div className="font-medium text-blue-900">
                  Selected: {selectedObjective.Name}
                </div>
                <div className="text-blue-700 text-xs mt-1">
                  Will be cloned as: {selectedObjective.Name} (Clone)
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
      <CreateProjectObjectiveModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          if (onSuccess) onSuccess();
        }}
        initialValues={initialForm || {}}
      />
    </>
  );
}
